import type { EventRoomVisibility } from '@pulsify/shared/dto'
import type { Event } from '~/shared/types'

/**
 * Who sees an event and who gets in. `request` ("Auf Anfrage") is listed for everyone but lets
 * people in only once the organizer accepted them. `private` is unlisted and invite-only.
 * `members` ("Nur Mitglieder") exists only for the organizer's active members. `publicWithInvites`
 * is a live public event that already has declines or plus-ones switched on from before those
 * became private-only; it keeps them until the organizer changes the choice.
 */
export type EventPrivacy = 'public' | 'request' | 'private' | 'members' | 'publicWithInvites'

type PrivacySource = Pick<Event, 'status' | 'isDraft' | 'rsvpEnabled' | 'plusOnesMax'> &
  Partial<Pick<Event, 'roomVisibility' | 'membersOnly'>>

const unlistedPrivacies: readonly EventPrivacy[] = ['private', 'members']

export function initialEventPrivacy(event: PrivacySource): EventPrivacy {
  if (event.membersOnly) return 'members'
  if (event.status === 'Unlisted') return 'private'
  if (event.roomVisibility === 'request') return 'request'
  const hasInviteOptions = event.rsvpEnabled || event.plusOnesMax > 0
  if (!hasInviteOptions) return 'public'
  const isDraft = event.isDraft || event.status === 'Draft' || event.status === 'Importing'
  return isDraft ? 'private' : 'publicWithInvites'
}

/**
 * Clears the private-only options of a listed event and sets the room visibility and the
 * members-only flag the backend stores next to the status. An event on request keeps its
 * companions limit ("Begleitung"); a members-only event keeps every invite option.
 */
export function applyEventPrivacy<
  T extends { privacy: EventPrivacy; rsvpEnabled: boolean; plusOnesMax: number },
>(values: T): T & { roomVisibility: EventRoomVisibility | null; membersOnly: boolean } {
  const roomVisibility = values.privacy === 'request' ? 'request' : null
  const membersOnly = values.privacy === 'members'
  if (
    values.privacy === 'private' ||
    values.privacy === 'members' ||
    values.privacy === 'publicWithInvites'
  ) {
    return { ...values, roomVisibility, membersOnly }
  }
  if (values.privacy === 'request') {
    return { ...values, rsvpEnabled: false, roomVisibility, membersOnly }
  }
  return { ...values, rsvpEnabled: false, plusOnesMax: 0, roomVisibility, membersOnly }
}

/** The status change a save of a live event needs so it matches the privacy choice. */
export function privacyStatusAction(
  privacy: EventPrivacy,
  event: Pick<Event, 'status' | 'isCanceled'>,
): 'publish' | 'unlist' | undefined {
  if (event.isCanceled) return undefined
  const unlisted = unlistedPrivacies.includes(privacy)
  if (unlisted && event.status === 'Published') return 'unlist'
  if (!unlisted && event.status === 'Unlisted') return 'publish'
  return undefined
}

/** The status a first publish gives the event. */
export function publishActionFor(privacy: EventPrivacy): 'publish' | 'unlist' {
  return unlistedPrivacies.includes(privacy) ? 'unlist' : 'publish'
}

/** Whether the visibility picker offers "Nur Mitglieder": the host takes members, or it is set. */
export function offersMembersPrivacy(
  membersEnabled: boolean | undefined,
  current: EventPrivacy,
): boolean {
  return membersEnabled === true || current === 'members'
}
